import { useEffect } from 'react';
import initialStudentBannerImage from '../assets/novas-banner-template.jpeg';
import { getStudentData } from '../assets/data';
import { getLessonListForStudent } from '../utils/lessonsService';
import { getStudentById, getStudents as getStudentsFromSupabase } from '../utils/studentsService';

// Функция для разбора строки price на currency и amount
function parsePrice(priceString) {
  if (!priceString || typeof priceString !== 'string') {
    return { currency: '$', amount: '' };
  }

  // Ищем валюту в начале строки (символы до первой цифры)
  const currencyMatch = priceString.match(/^([^\d\s]+)/);
  const currency = currencyMatch ? currencyMatch[1] : '$';

  // Ищем число в строке
  const amountMatch = priceString.match(/(\d+(?:\.\d+)?)/);
  const amount = amountMatch ? amountMatch[1] : '';

  return { currency, amount };
}

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). This is the most load-bearing piece of the
// whole component — it's what actually populates studentData on mount —
// moved byte-for-byte identical on purpose, given how many stale-data
// incidents this exact cache-first-then-Supabase fallback caused earlier
// in this project's history. No behavior changed, only where the code
// lives.
export function useStudentProfileLoader({
  studentId,
  isNew,
  bannerImageUrl,
  setBannerImageUrl,
  setStudentData,
  setStudentLessons,
  setLessonsLoading,
  updateUpcomingLessons,
}) {
  useEffect(() => {
    const loadStudentData = async () => {
      if (studentId === "new" || isNew) {
        setStudentLessons([]);
        setLessonsLoading(false);
        // Для нового студента устанавливаем дефолтный баннер
        if (!bannerImageUrl || bannerImageUrl === '' || bannerImageUrl === 'undefined' || bannerImageUrl === 'null') {
          if (initialStudentBannerImage) {
            setBannerImageUrl(initialStudentBannerImage);
            console.log('Set default banner for new student:', initialStudentBannerImage);
          } else {
            console.warn('initialStudentBannerImage is not available for new student');
          }
        }
        return;
      }

      if (!studentId) {
        setLessonsLoading(false);
        return;
      }

      // Сначала проверяем localStorage
      const localStudent = getStudentData()[studentId];

      if (localStudent) {
        // Данные есть в localStorage
        setStudentData(prev => ({
          ...prev,
          name: localStudent.name || "",
          dob: localStudent.dob || "",
          contactEmail: localStudent.contactEmail || "",
          contactPhone: localStudent.contactPhone || "",
          proficiency: localStudent.proficiency || "A1 - Elementary",
          experience: localStudent.experience || "",
          strengthsWeaknesses: localStudent.strengthsWeaknesses || "",
          price: localStudent.price || "",
          priceCurrency: localStudent.priceCurrency || (localStudent.price ? parsePrice(localStudent.price).currency : '$'),
          priceAmount: localStudent.priceAmount || (localStudent.price ? parsePrice(localStudent.price).amount : ''),
          status: localStudent.status ? localStudent.status.toLowerCase().replace(/\s/g, '_') : "active",
          goals: localStudent.goals || "",
          preferences: localStudent.preferences || "",
          hobbies: localStudent.hobbies || "",
          vacationFrom: localStudent.vacationFrom || "",
          vacationTo: localStudent.vacationTo || "",
          sickLeaveFrom: localStudent.sickLeaveFrom || "",
          sickLeaveTo: localStudent.sickLeaveTo || "",
          pausedResumeDate: localStudent.pausedResumeDate || "",
          lessons: localStudent.lessons || [],
          materials: localStudent.materials || [],
          inviteLink: localStudent.inviteLink || "",
          isRegistered: localStudent.isRegistered || false,
          createdAt: localStudent.createdAt || "",
          registeredAt: localStudent.registeredAt || "",
          userAccount: localStudent.userAccount || null,
        }));

        // Устанавливаем баннер из данных студента
        if (localStudent.bannerImage) {
          setBannerImageUrl(localStudent.bannerImage);
        } else {
          setBannerImageUrl(initialStudentBannerImage);
        }

        const studentIdForLessons = localStudent.studentId || localStudent.id || studentId;
        if (studentIdForLessons) {
          setLessonsLoading(true);
          getLessonListForStudent(studentIdForLessons).then(lessons => {
            console.log('=== LOADING LESSONS FOR STUDENT ===');
            console.log('studentId:', studentIdForLessons);
            console.log('Loaded lessons:', lessons);
            setStudentLessons(lessons);
            setLessonsLoading(false);
            updateUpcomingLessons();
          }).catch(error => {
            console.error('Error loading lessons:', error);
            setStudentLessons([]);
            setLessonsLoading(false);
          });
        } else {
          console.warn('No student ID found for loading lessons from localStorage');
          setStudentLessons([]);
          setLessonsLoading(false);
        }

        // Если у существующего студента нет inviteLink, генерируем его
        if (!localStudent.inviteLink) {
          const inviteLink = `${window.location.origin}/student-invite/${studentId}`;
          const updatedStudent = {
            ...localStudent,
            inviteLink,
            isRegistered: localStudent.isRegistered || false,
            createdAt: localStudent.createdAt || new Date().toISOString()
          };

          // Обновляем данные студента в localStorage
          const allStudents = getStudentData();
          allStudents[studentId] = updatedStudent;
          localStorage.setItem('novas_students_data', JSON.stringify(allStudents));

          // Обновляем состояние компонента
          setStudentData(prev => ({
            ...prev,
            inviteLink,
            isRegistered: localStudent.isRegistered || false,
            createdAt: localStudent.createdAt || new Date().toISOString()
          }));
        }
      } else {
        // Данных нет в localStorage - загружаем из Supabase
        console.log('Student not found in localStorage, loading from Supabase...');

        try {
          let studentFromSupabase = null;

          // Пробуем загрузить по ID (если studentId это UUID)
          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId);

          if (isUUID) {
            try {
              studentFromSupabase = await getStudentById(studentId);
              console.log('Loaded student by ID from Supabase:', studentFromSupabase);
            } catch (error) {
              console.log('Failed to load by ID, trying by slug...', error);
            }
          }

          // Если не загрузили по ID, пробуем загрузить всех студентов и найти по slug
          if (!studentFromSupabase) {
            const allStudents = await getStudentsFromSupabase();
            // Ищем студента по slug (studentId может быть slug)
            studentFromSupabase = Object.values(allStudents).find(
              s => s.slug === studentId || s.invite_slug === studentId || s.studentId === studentId
            );
            console.log('Found student by slug in Supabase:', studentFromSupabase);
          }

          if (studentFromSupabase) {
            // Конвертируем данные из Supabase в формат компонента
            const convertedStudent = {
              id: studentFromSupabase.id,
              studentId: studentFromSupabase.id || studentFromSupabase.studentId,
              slug: studentFromSupabase.slug || studentFromSupabase.invite_slug,
              name: studentFromSupabase.name || "",
              email: studentFromSupabase.email || "",
              phone: studentFromSupabase.phone || "",
              dob: studentFromSupabase.date_of_birth || studentFromSupabase.dob || "",
              contactEmail: studentFromSupabase.email || studentFromSupabase.contactEmail || "",
              contactPhone: studentFromSupabase.phone || studentFromSupabase.contactPhone || "",
              proficiency: studentFromSupabase.proficiency || "A1 - Elementary",
              experience: studentFromSupabase.experience || "",
              strengthsWeaknesses: studentFromSupabase.strengthsWeaknesses || "",
              price: studentFromSupabase.price || "",
              priceCurrency: studentFromSupabase.price_currency || studentFromSupabase.priceCurrency || (studentFromSupabase.price ? parsePrice(studentFromSupabase.price).currency : '$'),
              priceAmount: studentFromSupabase.priceAmount || (studentFromSupabase.price ? parsePrice(studentFromSupabase.price).amount : ''),
              status: studentFromSupabase.status || "active",
              goals: studentFromSupabase.goals || "",
              preferences: studentFromSupabase.preferences || "",
              hobbies: studentFromSupabase.hobbies || "",
              vacationFrom: studentFromSupabase.vacationFrom || "",
              vacationTo: studentFromSupabase.vacationTo || "",
              sickLeaveFrom: studentFromSupabase.sickLeaveFrom || "",
              sickLeaveTo: studentFromSupabase.sickLeaveTo || "",
              pausedResumeDate: studentFromSupabase.pausedResumeDate || "",
              lessons: studentFromSupabase.lessons || [],
              materials: studentFromSupabase.materials || [],
              links: studentFromSupabase.links || [],
              inviteLink: studentFromSupabase.invite_link || studentFromSupabase.inviteLink || "",
              isRegistered: !!studentFromSupabase.user_account_id || false,
              createdAt: studentFromSupabase.created_at || studentFromSupabase.createdAt || "",
              registeredAt: studentFromSupabase.registeredAt || "",
              userAccount: studentFromSupabase.userAccount || null,
              bannerImage: studentFromSupabase.bannerImage || initialStudentBannerImage,
              payments: Array.isArray(studentFromSupabase.payments) ? studentFromSupabase.payments : [],
            };

            // Сохраняем в localStorage для будущих загрузок
            const allStudents = getStudentData();
            const slug = convertedStudent.slug || studentId;
            allStudents[slug] = convertedStudent;
            localStorage.setItem('novas_students_data', JSON.stringify(allStudents));

            // Обновляем состояние компонента
            setStudentData(prev => ({
              ...prev,
              ...convertedStudent
            }));

            // Payments are no longer seeded here — usePaymentBalance's own
            // React Query fetch (keyed on studentSupabaseId) picks this
            // student up as soon as convertedStudent.id lands in
            // studentData above, and fetches payments straight from
            // Supabase itself.

            // Устанавливаем баннер
            if (convertedStudent.bannerImage) {
              setBannerImageUrl(convertedStudent.bannerImage);
            } else {
              setBannerImageUrl(initialStudentBannerImage);
            }

            const studentIdForLessons = convertedStudent.studentId || convertedStudent.id;
            if (studentIdForLessons) {
              setLessonsLoading(true);
              getLessonListForStudent(studentIdForLessons).then(lessons => {
                console.log('=== LOADING LESSONS FOR STUDENT FROM SUPABASE ===');
                console.log('studentId:', studentIdForLessons);
                console.log('Loaded lessons:', lessons);
                setStudentLessons(lessons);
                setLessonsLoading(false);
              }).catch(error => {
                console.error('Error loading lessons:', error);
                setStudentLessons([]);
                setLessonsLoading(false);
              });
            } else {
              console.warn('No student ID found for loading lessons');
              setStudentLessons([]);
              setLessonsLoading(false);
            }
          } else {
            console.warn('Student not found in Supabase either');
            setLessonsLoading(false);
          }
        } catch (error) {
          console.error('Error loading student from Supabase:', error);
          setLessonsLoading(false);
        }
      }
    };

    loadStudentData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, isNew]);

  // Listen for student data updates
  useEffect(() => {
    const handleStudentDataUpdate = () => {
      console.log('Student data updated in StudentProfile, reloading...');
      if (studentId && studentId !== "new" && !isNew && getStudentData()[studentId]) {
        const existingStudent = getStudentData()[studentId];
        console.log('Existing student from localStorage:', existingStudent);
        console.log('Existing student userAccount:', existingStudent.userAccount);
        console.log('Existing student userAccount.phone:', existingStudent.userAccount?.phone);

        setStudentData(prev => ({
          ...prev,
          name: existingStudent.name || "",
          dob: existingStudent.dob || "",
          contactEmail: existingStudent.contactEmail || "",
          contactPhone: existingStudent.contactPhone || "",
          proficiency: existingStudent.proficiency || "A1 - Elementary",
          experience: existingStudent.experience || "",
          strengthsWeaknesses: existingStudent.strengthsWeaknesses || "",
          price: existingStudent.price || "",
          status: existingStudent.status ? existingStudent.status.toLowerCase().replace(/\s/g, '_') : "active",
          goals: existingStudent.goals || "",
          preferences: existingStudent.preferences || "",
          hobbies: existingStudent.hobbies || "",
          vacationFrom: existingStudent.vacationFrom || "",
          vacationTo: existingStudent.vacationTo || "",
          sickLeaveFrom: existingStudent.sickLeaveFrom || "",
          sickLeaveTo: existingStudent.sickLeaveTo || "",
          pausedResumeDate: existingStudent.pausedResumeDate || "",
          lessons: existingStudent.lessons || [],
          materials: existingStudent.materials || [],
          inviteLink: existingStudent.inviteLink || "",
          isRegistered: existingStudent.isRegistered || false,
          createdAt: existingStudent.createdAt || "",
          registeredAt: existingStudent.registeredAt || "",
          userAccount: existingStudent.userAccount || null,
        }));

        // Устанавливаем баннер из данных студента
        if (existingStudent.bannerImage) {
          setBannerImageUrl(existingStudent.bannerImage);
        } else {
          setBannerImageUrl(initialStudentBannerImage);
        }

        console.log('Updated studentData in StudentProfile:', existingStudent);
      } else {
        console.log('Student not found or conditions not met:', { studentId, isNew, studentExists: !!getStudentData()[studentId] });
      }
    };

    window.addEventListener('studentDataUpdated', handleStudentDataUpdate);

    return () => {
      window.removeEventListener('studentDataUpdated', handleStudentDataUpdate);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, isNew]);
}
